import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Search,
  Filter,
  Mail,
  MessageSquare,
  AlertCircle,
  Clock,
  CheckCircle,
  User,
  Building,
  Calendar,
  Tag,
  Send,
  ExternalLink,
  RefreshCw,
  Eye,
  Edit3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSupportInbox = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    assignee: 'all',
    channel: 'all',
    hasJob: 'all',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [reply, setReply] = useState('');
  const [updating, setUpdating] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchTickets();
      fetchStats();
    }
  }, [user, filters]);

  useEffect(() => {
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id === ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
      } else {
        fetchTicketDetail(ticketId);
      }
    }
  }, [ticketId, tickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API}/admin/support/tickets?${params}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetail = async (id) => {
    try {
      const response = await axios.get(`${API}/admin/support/tickets/${id}`);
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Failed to fetch ticket detail:', error);
      toast.error('Failed to load ticket details');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/support/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTicketUpdate = async (ticketId, updateData) => {
    try {
      setUpdating(true);
      await axios.put(`${API}/admin/support/tickets/${ticketId}`, updateData);
      
      // Update local state
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, ...updateData, last_updated_at: new Date().toISOString() }
          : ticket
      ));
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ 
          ...prev, 
          ...updateData, 
          last_updated_at: new Date().toISOString() 
        }));
      }
      
      toast.success('Ticket updated successfully');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to update ticket:', error);
      toast.error('Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!reply.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setUpdating(true);
      await axios.post(`${API}/admin/support/tickets/${ticketId}/reply`, {
        message: reply
      });
      
      // Add reply to local state
      const newUpdate = {
        timestamp: new Date().toISOString(),
        message: reply,
        author: 'admin'
      };
      
      setSelectedTicket(prev => ({
        ...prev,
        customer_visible_updates: [...(prev.customer_visible_updates || []), newUpdate],
        status: 'pending',
        last_updated_at: new Date().toISOString()
      }));
      
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, status: 'pending', last_updated_at: new Date().toISOString() }
          : ticket
      ));
      
      setReply('');
      toast.success('Reply sent successfully');
      fetchStats();
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-red-100', text: 'text-red-800', label: 'Open' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' }
    };
    return badges[status] || badges.open;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Low' },
      normal: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Normal' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      urgent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Urgent' }
    };
    return badges[priority] || badges.normal;
  };

  const formatJobCode = (id) => {
    return `JOP-${id?.slice(0, 8).toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <MessageSquare className="h-6 w-6 mr-2" />
                  Support Inbox
                </h1>
                <p className="text-sm text-gray-600">Manage support tickets and customer inquiries</p>
              </div>
            </div>
            
            <button
              onClick={() => { fetchTickets(); fetchStats(); }}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedTicket ? (
          // Inbox View
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
              <div className="jop-card p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{stats.open || 0}</div>
                <div className="text-sm text-gray-600">Open</div>
              </div>
              <div className="jop-card p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
                <div className="text-sm text-gray-600">Pending</div>
              </div>
              <div className="jop-card p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.resolved || 0}</div>
                <div className="text-sm text-gray-600">Resolved</div>
              </div>
              <div className="jop-card p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.urgent || 0}</div>
                <div className="text-sm text-gray-600">Urgent</div>
              </div>
              <div className="jop-card p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
            </div>

            {/* Filters */}
            <div className="jop-card p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="jop-select"
                  >
                    <option value="all">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    className="jop-select"
                  >
                    <option value="all">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Has Job</label>
                  <select
                    value={filters.hasJob}
                    onChange={(e) => handleFilterChange('hasJob', e.target.value)}
                    className="jop-select"
                  >
                    <option value="all">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="jop-input pl-10"
                      placeholder="Subject, message, business..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets Table */}
            <div className="jop-card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Support Tickets</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tickets.map((ticket) => (
                      <tr 
                        key={ticket.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          navigate(`/admin/support/${ticket.id}`);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(ticket.created_at)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(ticket.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                            {ticket.subject}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{ticket.id.slice(0, 8)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {ticket.sender_business}
                              </div>
                              <div className="text-xs text-gray-500">
                                {ticket.sender_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {ticket.job_id ? (
                            <div className="text-sm text-blue-600 font-medium">
                              {formatJobCode(ticket.job_id)}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityBadge(ticket.priority).bg} ${getPriorityBadge(ticket.priority).text}`}>
                            {getPriorityBadge(ticket.priority).label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(ticket.status).bg} ${getStatusBadge(ticket.status).text}`}>
                            {getStatusBadge(ticket.status).label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(ticket.last_updated_at)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {tickets.length === 0 && (
                  <div className="text-center py-12">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your filters to see more tickets.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          // Ticket Detail View - This will be a separate component to avoid making this file too large
          <div className="text-center py-12">
            <p className="text-gray-500">Ticket detail view will be implemented as a separate component</p>
            <button 
              onClick={() => {
                setSelectedTicket(null);
                navigate('/admin/support');
              }}
              className="jop-button mt-4"
            >
              Back to Inbox
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupportInbox;